/**
 * CimetiereManager — gestion du cimetière (mairie / GRC).
 *
 * Onglets : Registre (concessions + recherche + défunts + titre PDF + Excel),
 * Échéances (concessions échéant bientôt — aide obligation légale d'info),
 * Demandes (demandes citoyen à traiter).
 *
 * Conforme au guide : zéro window.confirm (useConfirmDialog + {dialogElement}).
 */

import { useEffect, useState, useCallback } from 'react';
import { Button, Badge, Alert, Field, Input } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { api, ApiException } from '@/lib/api';
import type {
  Cimetiere, Concession, ConcessionDefunt, ConcessionDemande, CimetiereEtatMairie,
  TypeDuree, TypeEmplacement, ConcessionStatut,
} from '@/lib/api';
import {
  Plus, Pencil, Trash2, FileText, Download, Search, AlertTriangle, AlertCircle,
  MapPin, User, Calendar, Building2, CheckCircle2, X, Users,
} from 'lucide-react';

const TYPE_DUREE_LABEL: Record<TypeDuree, string> = {
  temporaire: 'Temporaire', trentenaire: 'Trentenaire', cinquantenaire: 'Cinquantenaire', perpetuelle: 'Perpétuelle',
};
const TYPE_EMPL_LABEL: Record<TypeEmplacement, string> = {
  pleine_terre: 'Pleine terre', caveau: 'Caveau', columbarium: 'Columbarium', cavurne: 'Cavurne', jardin_souvenir: 'Jardin du souvenir',
};
const STATUT_LABEL: Record<ConcessionStatut, string> = {
  active: 'Active', echue: 'Échue (renouvelable)', delai_reprise: 'Reprise possible', reprise: 'Reprise',
};
const STATUT_VARIANT: Record<ConcessionStatut, 'success' | 'warning' | 'urgent' | 'neutral'> = {
  active: 'success', echue: 'warning', delai_reprise: 'urgent', reprise: 'neutral',
};

function frDate(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d!)).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });
}

function emplacementTxt(c: Concession): string {
  return [
    c.emplacementCarre ? `Carré ${c.emplacementCarre}` : null,
    c.emplacementRang ? `Rang ${c.emplacementRang}` : null,
    c.emplacementNumero ? `N° ${c.emplacementNumero}` : null,
  ].filter(Boolean).join(' · ') || '—';
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

export function CimetiereManager({ onEtatChange }: { onEtatChange?: () => void }) {
  const { confirm, dialogElement } = useConfirmDialog();
  const [onglet, setOnglet] = useState<'registre' | 'echeances' | 'demandes'>('registre');
  const [etat, setEtat] = useState<CimetiereEtatMairie | null>(null);
  const [cimetieres, setCimetieres] = useState<Cimetiere[]>([]);
  const [concessions, setConcessions] = useState<Concession[]>([]);
  const [echeances, setEcheances] = useState<Concession[]>([]);
  const [demandes, setDemandes] = useState<ConcessionDemande[]>([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [cimetiereEdit, setCimetiereEdit] = useState<Cimetiere | 'new' | null>(null);
  const [concessionEdit, setConcessionEdit] = useState<Concession | 'new' | null>(null);
  const [detailConcession, setDetailConcession] = useState<Concession | null>(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [e, cim, conc, ech, dem] = await Promise.all([
        api.grc.cimetiere.etat(),
        api.grc.cimetiere.listCimetieres(),
        api.grc.cimetiere.listConcessions(),
        api.grc.cimetiere.echeances(12),
        api.grc.cimetiere.listDemandes(),
      ]);
      setEtat(e); setCimetieres(cim.cimetieres); setConcessions(conc.concessions);
      setEcheances(ech.concessions); setDemandes(dem.demandes);
    } catch (err) {
      setError(err instanceof ApiException ? err.message : 'Chargement impossible');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { void reload(); }, [reload]);
  async function refreshAll() { await reload(); onEtatChange?.(); }

  async function rechercher() {
    try { setConcessions((await api.grc.cimetiere.listConcessions({ q: q.trim() || undefined })).concessions); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Recherche impossible'); }
  }

  async function supprimerConcession(c: Concession) {
    const ok = await confirm({ title: 'Supprimer cette concession ?', description: `N° ${c.numero} — ${c.concessionnaireNom}. Les défunts associés seront aussi supprimés.`, confirmLabel: 'Supprimer', danger: true });
    if (!ok) return;
    try { await api.grc.cimetiere.removeConcession(c.id); await refreshAll(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Suppression impossible'); }
  }

  async function telechargerTitre(c: Concession) {
    try { downloadBlob(await api.grc.cimetiere.titrePdfBlob(c.id), `titre-concession-${c.numero}.pdf`); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'PDF indisponible'); }
  }
  async function exporterExcel() {
    try { downloadBlob(await api.grc.cimetiere.exportXlsxBlob(), 'concessions.xlsx'); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Export indisponible'); }
  }

  async function traiterDemande(d: ConcessionDemande, statut: 'traitee' | 'refusee') {
    const ok = await confirm({
      title: statut === 'traitee' ? 'Marquer traitée ?' : 'Refuser la demande ?',
      description: `Demande de ${d.typeDemande === 'renouvellement' ? 'renouvellement' : 'nouvelle concession'} — ${d.demandeurNom ?? 'administré'}.`,
      confirmLabel: statut === 'traitee' ? 'Traitée' : 'Refuser', danger: statut === 'refusee',
    });
    if (!ok) return;
    try { await api.grc.cimetiere.traiterDemande(d.id, statut); await refreshAll(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Action impossible'); }
  }

  const tabCls = (active: boolean) => active
    ? 'rounded-r-pill border border-grc/30 bg-grc-tint/80 px-3 py-1 text-xs font-medium text-grc-fg'
    : 'rounded-r-pill border border-transparent px-3 py-1 text-xs font-medium text-ink-muted hover:border-border hover:text-ink';

  const demandesEnAttente = demandes.filter((d) => d.statut === 'nouvelle' || d.statut === 'en_cours').length;

  return (
    <div>
      {error && <Alert variant="error" icon={AlertCircle} className="mb-3">{error}</Alert>}

      {etat && etat.aEcheance > 0 && (
        <Alert variant="warning" icon={AlertTriangle} className="mb-3">
          {etat.aEcheance} concession{etat.aEcheance > 1 ? 's' : ''} arrive{etat.aEcheance > 1 ? 'nt' : ''} à échéance sous 12 mois.
          Pensez à informer les concessionnaires de leur droit de renouvellement (obligation légale).
        </Alert>
      )}

      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-1.5">
          <button type="button" onClick={() => setOnglet('registre')} className={tabCls(onglet === 'registre')}>Registre</button>
          <button type="button" onClick={() => setOnglet('echeances')} className={tabCls(onglet === 'echeances')}>
            Échéances{echeances.length > 0 ? ` (${echeances.length})` : ''}
          </button>
          <button type="button" onClick={() => setOnglet('demandes')} className={tabCls(onglet === 'demandes')}>
            Demandes{demandesEnAttente > 0 ? ` (${demandesEnAttente})` : ''}
          </button>
        </div>
        {onglet === 'registre' && (
          <div className="flex gap-1.5">
            <Button variant="ghost" size="sm" onClick={() => setCimetiereEdit('new')}><Building2 className="h-4 w-4" /> Cimetière</Button>
            <Button variant="ghost" size="sm" onClick={() => setConcessionEdit('new')} disabled={cimetieres.length === 0}><Plus className="h-4 w-4" /> Concession</Button>
            <Button variant="ghost" size="sm" onClick={exporterExcel}><Download className="h-4 w-4" /> Excel</Button>
          </div>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-ink-muted">Chargement…</p>
      ) : onglet === 'registre' ? (
        <div>
          {cimetieres.length === 0 ? (
            <p className="text-sm text-ink-muted">Commencez par créer un cimetière, puis ajoutez des concessions.</p>
          ) : (
            <>
              <div className="mb-2 flex flex-wrap gap-1.5">
                {cimetieres.map((cim) => (
                  <span key={cim.id} className="inline-flex items-center gap-1 rounded-r-pill border border-border bg-bg-2 px-2 py-0.5 text-xs text-ink-muted">
                    <Building2 className="h-3 w-3" />{cim.nom}
                    <button type="button" onClick={() => setCimetiereEdit(cim)} className="text-ink-dim hover:text-ink" title="Modifier"><Pencil className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
              <div className="mb-2 flex gap-2">
                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-dim" />
                  <input
                    className="w-full rounded-r-md border border-border bg-bg py-1.5 pl-8 pr-3 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc"
                    placeholder="Rechercher (nom, n° concession)…" value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') void rechercher(); }}
                  />
                </div>
                <Button variant="ghost" size="sm" onClick={rechercher}>Rechercher</Button>
              </div>
              {concessions.length === 0 ? (
                <p className="text-sm text-ink-muted">Aucune concession.</p>
              ) : (
                <ul className="space-y-2">
                  {concessions.map((c) => (
                    <li key={c.id} className="rounded-r-lg border border-border bg-surface/50 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <button type="button" className="min-w-0 flex-1 text-left" onClick={() => setDetailConcession(c)}>
                          <div className="flex items-center gap-2 font-medium text-ink">
                            <span>N° {c.numero}</span>
                            <Badge variant={STATUT_VARIANT[c.statutEffectif]} size="sm">{STATUT_LABEL[c.statutEffectif]}</Badge>
                          </div>
                          <div className="mt-0.5 inline-flex items-center gap-1 text-sm text-ink-muted"><User className="h-3 w-3" />{c.concessionnaireNom}</div>
                          <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-ink-dim">
                            <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{emplacementTxt(c)}</span>
                            <span>{TYPE_DUREE_LABEL[c.typeDuree]}</span>
                            <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />Échéance : {frDate(c.dateEcheance)}</span>
                          </div>
                        </button>
                        <div className="flex flex-shrink-0 gap-1">
                          <Button variant="ghost" size="sm" onClick={() => telechargerTitre(c)} title="Titre PDF"><FileText className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => setConcessionEdit(c)} title="Modifier"><Pencil className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => void supprimerConcession(c)} title="Supprimer"><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      ) : onglet === 'echeances' ? (
        echeances.length === 0 ? (
          <p className="text-sm text-ink-muted">Aucune concession à échéance dans les 12 prochains mois.</p>
        ) : (
          <ul className="space-y-2">
            {echeances.map((c) => (
              <li key={c.id} className="flex items-start justify-between gap-3 rounded-r-lg border border-border bg-surface/50 p-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 font-medium text-ink">N° {c.numero}
                    <Badge variant={STATUT_VARIANT[c.statutEffectif]} size="sm">{STATUT_LABEL[c.statutEffectif]}</Badge>
                  </div>
                  <div className="mt-0.5 text-sm text-ink-muted">{c.concessionnaireNom}</div>
                  <div className="mt-0.5 text-xs text-ink-dim">Échéance : {frDate(c.dateEcheance)}
                    {(c.ayantDroitEmail || c.concessionnaireEmail) ? ` · Contact : ${c.ayantDroitEmail ?? c.concessionnaireEmail}` : ''}</div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => telechargerTitre(c)} title="Titre PDF"><FileText className="h-4 w-4" /></Button>
              </li>
            ))}
          </ul>
        )
      ) : (
        demandes.length === 0 ? (
          <p className="text-sm text-ink-muted">Aucune demande.</p>
        ) : (
          <ul className="space-y-2">
            {demandes.map((d) => (
              <li key={d.id} className="rounded-r-lg border border-border bg-surface/50 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 font-medium text-ink">
                      {d.typeDemande === 'renouvellement' ? 'Renouvellement' : 'Nouvelle concession'}
                      {d.statut === 'nouvelle' && <Badge variant="warning" size="sm">Nouvelle</Badge>}
                      {d.statut === 'en_cours' && <Badge variant="neutral" size="sm">En cours</Badge>}
                      {d.statut === 'traitee' && <Badge variant="success" size="sm">Traitée</Badge>}
                      {d.statut === 'refusee' && <Badge variant="urgent" size="sm">Refusée</Badge>}
                    </div>
                    <div className="mt-0.5 text-sm text-ink-muted">{d.demandeurNom ?? 'Administré'}{d.concessionNumero ? ` · Concession n° ${d.concessionNumero}` : ''}</div>
                    {d.message && <p className="mt-0.5 text-xs italic text-ink-dim">« {d.message} »</p>}
                    <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-ink-dim">
                      {d.demandeurEmail && <span>{d.demandeurEmail}</span>}
                      {d.demandeurTel && <span>{d.demandeurTel}</span>}
                    </div>
                  </div>
                  {(d.statut === 'nouvelle' || d.statut === 'en_cours') && (
                    <div className="flex flex-shrink-0 gap-1">
                      <Button variant="ghost" size="sm" onClick={() => void traiterDemande(d, 'traitee')} title="Traitée"><CheckCircle2 className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => void traiterDemande(d, 'refusee')} title="Refuser"><X className="h-4 w-4" /></Button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )
      )}

      {cimetiereEdit && (
        <CimetiereDialog initial={cimetiereEdit === 'new' ? null : cimetiereEdit}
          onClose={() => setCimetiereEdit(null)} onSaved={async () => { setCimetiereEdit(null); await refreshAll(); }} />
      )}
      {concessionEdit && (
        <ConcessionDialog initial={concessionEdit === 'new' ? null : concessionEdit} cimetieres={cimetieres}
          onClose={() => setConcessionEdit(null)} onSaved={async () => { setConcessionEdit(null); await refreshAll(); }} />
      )}
      {detailConcession && (
        <DetailConcessionDialog concessionId={detailConcession.id}
          onClose={() => setDetailConcession(null)} onChanged={refreshAll} confirm={confirm} />
      )}
      {dialogElement}
    </div>
  );
}

// ============================================================================
function CimetiereDialog({ initial, onClose, onSaved }: { initial: Cimetiere | null; onClose: () => void; onSaved: () => void }) {
  const [nom, setNom] = useState(initial?.nom ?? '');
  const [adresse, setAdresse] = useState(initial?.adresse ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function save() {
    setBusy(true); setErr(null);
    try {
      const body = { nom: nom.trim(), adresse: adresse.trim() || null };
      if (initial) await api.grc.cimetiere.updateCimetiere(initial.id, body);
      else await api.grc.cimetiere.createCimetiere(body);
      onSaved();
    } catch (e) { setErr(e instanceof ApiException ? e.message : 'Enregistrement impossible'); setBusy(false); }
  }
  return (
    <Dialog open onClose={onClose} title={initial ? 'Modifier le cimetière' : 'Nouveau cimetière'}>
      <div className="space-y-3">
        {err && <Alert variant="error" icon={AlertCircle}>{err}</Alert>}
        <Field label="Nom" htmlFor="cim-nom" required>
          <Input id="cim-nom" value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Cimetière communal" />
        </Field>
        <Field label="Adresse (optionnel)" htmlFor="cim-adr">
          <Input id="cim-adr" value={adresse} onChange={(e) => setAdresse(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button variant="primary" onClick={save} disabled={busy || !nom.trim()}>{busy ? 'Enregistrement…' : 'Enregistrer'}</Button>
        </div>
      </div>
    </Dialog>
  );
}

// ============================================================================
function ConcessionDialog({ initial, cimetieres, onClose, onSaved }: {
  initial: Concession | null; cimetieres: Cimetiere[]; onClose: () => void; onSaved: () => void;
}) {
  const [cimetiereId, setCimetiereId] = useState(initial?.cimetiereId ?? cimetieres[0]?.id ?? '');
  const [numero, setNumero] = useState(initial?.numero ?? '');
  const [carre, setCarre] = useState(initial?.emplacementCarre ?? '');
  const [rang, setRang] = useState(initial?.emplacementRang ?? '');
  const [numeroEmpl, setNumeroEmpl] = useState(initial?.emplacementNumero ?? '');
  const [typeEmplacement, setTypeEmplacement] = useState<TypeEmplacement>(initial?.typeEmplacement ?? 'pleine_terre');
  const [concessionnaireNom, setConcessionnaireNom] = useState(initial?.concessionnaireNom ?? '');
  const [concessionnaireEmail, setConcessionnaireEmail] = useState(initial?.concessionnaireEmail ?? '');
  const [concessionnaireTel, setConcessionnaireTel] = useState(initial?.concessionnaireTel ?? '');
  const [ayantDroitNom, setAyantDroitNom] = useState(initial?.ayantDroitNom ?? '');
  const [ayantDroitEmail, setAyantDroitEmail] = useState(initial?.ayantDroitEmail ?? '');
  const [typeDuree, setTypeDuree] = useState<TypeDuree>(initial?.typeDuree ?? 'trentenaire');
  const [dureeAnnees, setDureeAnnees] = useState<number>(initial?.dureeAnnees ?? 15);
  const [dateDebut, setDateDebut] = useState(initial?.dateDebut ?? new Date().toISOString().slice(0, 10));
  const [tarifEuros, setTarifEuros] = useState<string>(initial?.tarifEuros != null ? String(initial.tarifEuros) : '');
  const [statut, setStatut] = useState<ConcessionStatut>(initial?.statut ?? 'active');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const selectCls = 'w-full rounded-r-md border border-border bg-bg px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc';

  async function save() {
    setBusy(true); setErr(null);
    try {
      const body: Record<string, unknown> = {
        cimetiereId, numero: numero.trim(),
        emplacementCarre: carre.trim() || null, emplacementRang: rang.trim() || null, emplacementNumero: numeroEmpl.trim() || null,
        typeEmplacement, concessionnaireNom: concessionnaireNom.trim(),
        concessionnaireEmail: concessionnaireEmail.trim() || null, concessionnaireTel: concessionnaireTel.trim() || null,
        ayantDroitNom: ayantDroitNom.trim() || null, ayantDroitEmail: ayantDroitEmail.trim() || null,
        typeDuree, dateDebut,
        dureeAnnees: typeDuree === 'temporaire' ? dureeAnnees : (typeDuree === 'perpetuelle' ? null : undefined),
        tarifEuros: tarifEuros.trim() ? Number(tarifEuros) : null,
        statut,
      };
      if (initial) await api.grc.cimetiere.updateConcession(initial.id, body);
      else await api.grc.cimetiere.createConcession(body);
      onSaved();
    } catch (e) { setErr(e instanceof ApiException ? e.message : 'Enregistrement impossible'); setBusy(false); }
  }

  return (
    <Dialog open onClose={onClose} title={initial ? 'Modifier la concession' : 'Nouvelle concession'}>
      <div className="max-h-[70vh] space-y-3 overflow-y-auto pr-1">
        {err && <Alert variant="error" icon={AlertCircle}>{err}</Alert>}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Cimetière" htmlFor="c-cim" required>
            <select id="c-cim" className={selectCls} value={cimetiereId} onChange={(e) => setCimetiereId(e.target.value)}>
              {cimetieres.map((cim) => <option key={cim.id} value={cim.id}>{cim.nom}</option>)}
            </select>
          </Field>
          <Field label="N° de concession" htmlFor="c-num" required>
            <Input id="c-num" value={numero} onChange={(e) => setNumero(e.target.value)} />
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Field label="Carré" htmlFor="c-carre"><Input id="c-carre" value={carre} onChange={(e) => setCarre(e.target.value)} /></Field>
          <Field label="Rang" htmlFor="c-rang"><Input id="c-rang" value={rang} onChange={(e) => setRang(e.target.value)} /></Field>
          <Field label="N°" htmlFor="c-nempl"><Input id="c-nempl" value={numeroEmpl} onChange={(e) => setNumeroEmpl(e.target.value)} /></Field>
        </div>
        <Field label="Type d'emplacement" htmlFor="c-empl">
          <select id="c-empl" className={selectCls} value={typeEmplacement} onChange={(e) => setTypeEmplacement(e.target.value as TypeEmplacement)}>
            {Object.entries(TYPE_EMPL_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>
        <Field label="Concessionnaire (titulaire)" htmlFor="c-titu" required>
          <Input id="c-titu" value={concessionnaireNom} onChange={(e) => setConcessionnaireNom(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Email titulaire" htmlFor="c-email"><Input id="c-email" type="email" value={concessionnaireEmail} onChange={(e) => setConcessionnaireEmail(e.target.value)} /></Field>
          <Field label="Téléphone" htmlFor="c-tel"><Input id="c-tel" value={concessionnaireTel} onChange={(e) => setConcessionnaireTel(e.target.value)} /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Ayant droit / contact renouvellement" htmlFor="c-ad"><Input id="c-ad" value={ayantDroitNom} onChange={(e) => setAyantDroitNom(e.target.value)} /></Field>
          <Field label="Email ayant droit" htmlFor="c-ademail"><Input id="c-ademail" type="email" value={ayantDroitEmail} onChange={(e) => setAyantDroitEmail(e.target.value)} /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nature" htmlFor="c-dur" required>
            <select id="c-dur" className={selectCls} value={typeDuree} onChange={(e) => setTypeDuree(e.target.value as TypeDuree)}>
              {Object.entries(TYPE_DUREE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          {typeDuree === 'temporaire' && (
            <Field label="Durée (5-15 ans)" htmlFor="c-annees" required>
              <Input id="c-annees" type="number" min={5} max={15} value={dureeAnnees} onChange={(e) => setDureeAnnees(Number(e.target.value))} />
            </Field>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date d'effet" htmlFor="c-date" required><Input id="c-date" type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} /></Field>
          <Field label="Redevance (€)" htmlFor="c-tarif"><Input id="c-tarif" type="number" min={0} step="0.01" value={tarifEuros} onChange={(e) => setTarifEuros(e.target.value)} /></Field>
        </div>
        <Field label="Statut" htmlFor="c-stat">
          <select id="c-stat" className={selectCls} value={statut} onChange={(e) => setStatut(e.target.value as ConcessionStatut)}>
            <option value="active">Active</option>
            <option value="reprise">Reprise</option>
          </select>
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button variant="primary" onClick={save} disabled={busy || !numero.trim() || !concessionnaireNom.trim()}>{busy ? 'Enregistrement…' : 'Enregistrer'}</Button>
        </div>
      </div>
    </Dialog>
  );
}

// ============================================================================
function DetailConcessionDialog({ concessionId, onClose, onChanged, confirm }: {
  concessionId: string; onClose: () => void; onChanged: () => void;
  confirm: (o: { title: string; description?: string; confirmLabel?: string; danger?: boolean }) => Promise<boolean>;
}) {
  const [concession, setConcession] = useState<Concession | null>(null);
  const [ajout, setAjout] = useState(false);
  const [nom, setNom] = useState(''); const [prenom, setPrenom] = useState('');
  const [dateDeces, setDateDeces] = useState(''); const [dateInhum, setDateInhum] = useState('');
  const [typeInhum, setTypeInhum] = useState<'inhumation' | 'depot_urne' | 'dispersion'>('inhumation');
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setConcession(await api.grc.cimetiere.getConcession(concessionId)); }
    catch (e) { setErr(e instanceof ApiException ? e.message : 'Chargement impossible'); }
  }, [concessionId]);
  useEffect(() => { void load(); }, [load]);

  async function ajouterDefunt() {
    setBusy(true); setErr(null);
    try {
      await api.grc.cimetiere.addDefunt(concessionId, {
        nom: nom.trim(), prenom: prenom.trim() || null,
        dateDeces: dateDeces || null, dateInhumation: dateInhum || null, typeInhumation: typeInhum,
      });
      setNom(''); setPrenom(''); setDateDeces(''); setDateInhum(''); setAjout(false);
      await load(); onChanged();
    } catch (e) { setErr(e instanceof ApiException ? e.message : 'Ajout impossible'); }
    finally { setBusy(false); }
  }
  async function supprimerDefunt(d: ConcessionDefunt) {
    const ok = await confirm({ title: 'Retirer ce défunt ?', description: `${d.nom} ${d.prenom ?? ''}`.trim(), confirmLabel: 'Retirer', danger: true });
    if (!ok) return;
    try { await api.grc.cimetiere.removeDefunt(d.id); await load(); onChanged(); }
    catch (e) { setErr(e instanceof ApiException ? e.message : 'Suppression impossible'); }
  }

  const selectCls = 'w-full rounded-r-md border border-border bg-bg px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc';

  return (
    <Dialog open onClose={onClose} title={concession ? `Concession n° ${concession.numero}` : 'Concession'}>
      <div className="max-h-[70vh] space-y-3 overflow-y-auto pr-1">
        {err && <Alert variant="error" icon={AlertCircle}>{err}</Alert>}
        {concession && (
          <>
            <div className="rounded-r-lg border border-border bg-surface/50 p-3 text-sm">
              <div className="text-ink"><span className="text-ink-dim">Titulaire :</span> {concession.concessionnaireNom}</div>
              <div className="text-ink"><span className="text-ink-dim">Emplacement :</span> {emplacementTxt(concession)}</div>
              <div className="text-ink"><span className="text-ink-dim">Nature :</span> {TYPE_DUREE_LABEL[concession.typeDuree]} · échéance {frDate(concession.dateEcheance)}</div>
            </div>
            <div className="flex items-center justify-between">
              <div className="inline-flex items-center gap-1 text-sm font-medium text-ink"><Users className="h-4 w-4 text-grc" /> Défunts inhumés ({concession.defunts?.length ?? 0})</div>
              {!ajout && <Button variant="ghost" size="sm" onClick={() => setAjout(true)}><Plus className="h-4 w-4" /> Ajouter</Button>}
            </div>
            {ajout && (
              <div className="space-y-2 rounded-r-lg border border-border bg-bg-2 p-3">
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Nom" htmlFor="d-nom" required><Input id="d-nom" value={nom} onChange={(e) => setNom(e.target.value)} /></Field>
                  <Field label="Prénom" htmlFor="d-prenom"><Input id="d-prenom" value={prenom} onChange={(e) => setPrenom(e.target.value)} /></Field>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Date de décès" htmlFor="d-deces"><Input id="d-deces" type="date" value={dateDeces} onChange={(e) => setDateDeces(e.target.value)} /></Field>
                  <Field label="Date d'inhumation" htmlFor="d-inhum"><Input id="d-inhum" type="date" value={dateInhum} onChange={(e) => setDateInhum(e.target.value)} /></Field>
                </div>
                <Field label="Type" htmlFor="d-type">
                  <select id="d-type" className={selectCls} value={typeInhum} onChange={(e) => setTypeInhum(e.target.value as 'inhumation' | 'depot_urne' | 'dispersion')}>
                    <option value="inhumation">Inhumation</option>
                    <option value="depot_urne">Dépôt d'urne</option>
                    <option value="dispersion">Dispersion</option>
                  </select>
                </Field>
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setAjout(false)} disabled={busy}>Annuler</Button>
                  <Button variant="primary" size="sm" onClick={ajouterDefunt} disabled={busy || !nom.trim()}>Ajouter</Button>
                </div>
              </div>
            )}
            {(concession.defunts?.length ?? 0) > 0 && (
              <ul className="space-y-1.5">
                {concession.defunts!.map((d) => (
                  <li key={d.id} className="flex items-center justify-between gap-2 rounded-r-md border border-border bg-surface/50 px-3 py-2 text-sm">
                    <div className="min-w-0">
                      <span className="text-ink">{d.nom} {d.prenom ?? ''}</span>
                      <span className="ml-2 text-xs text-ink-dim">{d.dateDeces ? `† ${frDate(d.dateDeces)}` : ''}{d.dateInhumation ? ` · inh. ${frDate(d.dateInhumation)}` : ''}</span>
                    </div>
                    <button type="button" onClick={() => void supprimerDefunt(d)} className="text-ink-dim hover:text-urgent" title="Retirer"><Trash2 className="h-4 w-4" /></button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
        <div className="flex justify-end pt-2"><Button variant="ghost" onClick={onClose}>Fermer</Button></div>
      </div>
    </Dialog>
  );
}
